/**
 * Import React libraries.
 */
import * as React from 'react';

/**
 * Import custom styles.
 */
import { COLORS } from '../styles';

/**
 * Import third-party libraries.
 */
import styled from 'styled-components';

// Add custom styles
const HorizontalTabContainer = styled.div`
  width: 100%;
  display: flex;
  position: relative;
  flex-direction: row;
`;

const TabSideFill = styled.div`
  position: absolute;
  border-bottom: 1px solid ${COLORS.neutral_100};
  height: Calc(100% - 1px);
  z-index: 1;
  width: 100%;

  &.fill {
    background: ${COLORS.white};
  }
`;

const TabButton = styled.button`
  color: ${COLORS.neutral_800};
  border: none;
  background-color: ${COLORS.white};
  z-index: 2;
  cursor: pointer;
  border-bottom: 1px solid ${COLORS.neutral_100};

  &:hover {
    background: ${COLORS.primary_20};
    color: ${COLORS.primary_700};
  }

  &:focus {
    background: ${COLORS.primary_100};
    color: ${COLORS.primary_800};
    box-shadow: 0px 4px 12px ${COLORS.focus_25}, 0px 0px 8px ${COLORS.focus} !important;
    outline: none;
  }

  &.disabled {
    background: ${COLORS.white};
    color: ${COLORS.neutral_300};
    cursor: not-allowed;
    box-shadow: none !important;
  }
  
  &.small {
    font-size: 12px;
    font-weight: 400;
    padding: 4px 16px;

    &.selected {
      border-bottom: 2px solid ${COLORS.primary_500};
    }
  }

  &.medium {
    font-size: 16px;
    font-weight: 400;
    height: 40px;
    min-width: 96px;
    padding: 0px 16px;

    &.selected {
      border-top: 3px solid ${COLORS.primary_500};
      border-bottom: 0px;
      border-left: 1px solid ${COLORS.neutral_100};
      border-right: 1px solid ${COLORS.neutral_100};
    }
  }

  &.large {
    font-size: 18px;
    font-weight: 400;
    height: 40px;
    min-width: 144px;
    padding: 0px 16px;

    &.selected {
      border-top: 3px solid ${COLORS.primary_500};
      border-bottom: 0px;
      border-left: 1px solid ${COLORS.neutral_100};
      border-right: 1px solid ${COLORS.neutral_100};
    }
  }
`;

// Add component-specific types
interface HorizontalTabProps {
  size: HorizontalTabSizes;
  tabs: HorizontalTab[];
  sideFill?: boolean;
  onTabChange: (to: string) => void; 
};

interface HorizontalTab {
  value: string;
  selected: boolean;
  to: string;
  disabled?: boolean;
}

type HorizontalTabSizes = 'small' | 'medium' | 'large';

const HorizontalTabs = ({ size, tabs, sideFill, onTabChange }: HorizontalTabProps) => {
  /**
   * Informs parent component of tab change and clears focus.
   * @param to - A link to which user should be navigated.
   */
  const doTabChange = (to: string): void => {
    // Let's inform parent component
    onTabChange(to);

    // Let's clear focus
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement!.blur();
    }
  };

  return (
    <HorizontalTabContainer>
      <TabSideFill className={`${sideFill ? 'fill' : ''}`} />
      {
        tabs.map((tab: HorizontalTab, index: number) => (
          <TabButton key={`${tab}_${index}`} className={`${size} ${tab.selected && 'selected'} ${tab.disabled && 'disabled'}`} onClick={() => !tab.disabled && doTabChange(tab.to)}>{ tab.value }</TabButton>
        ))
      }
    </HorizontalTabContainer>
  );
};

export default HorizontalTabs;
